import { useEffect, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { fetchAllChannels, withCatalogChannels } from '../../lib/channels';

export default function ChannelsDebugView() {
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof fetchAllChannels>>>([]);
  const [discovered, setDiscovered] = useState<string[]>([]);
  const [perChannelCounts, setPerChannelCounts] = useState<Record<string, number>>({});
  const [totalActive, setTotalActive] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const allChannels = await fetchAllChannels();
      setCatalog(allChannels);

      const { data, error: selErr } = await supabase
        .from('subscribers')
        .select('channels, email_status, unsubscribed_at')
        .limit(10000);
      if (selErr) throw selErr;

      const set = new Set<string>();
      const counts: Record<string, number> = {};
      let active = 0;

      for (const row of (data ?? []) as Array<{
        channels: unknown;
        email_status: string | null;
        unsubscribed_at: string | null;
      }>) {
        const isActive = !row.unsubscribed_at && row.email_status === 'confirmed';
        if (isActive) active += 1;

        const ch = row.channels;
        if (ch && typeof ch === 'object' && !Array.isArray(ch)) {
          for (const [k, v] of Object.entries(ch as Record<string, unknown>)) {
            if (v !== true) continue;
            const key = k.trim();
            if (!key) continue;
            set.add(key);
            if (isActive) counts[key] = (counts[key] ?? 0) + 1;
          }
        }
      }

      setDiscovered([...set].sort());
      setPerChannelCounts(counts);
      setTotalActive(active);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const merged = withCatalogChannels(discovered, catalog.filter((c) => c.active));

  const payload = {
    generated_at: new Date().toISOString(),
    summary: {
      catalog_count: catalog.length,
      discovered_count: discovered.length,
      merged_count: merged.length,
      active_confirmed_subscribers: totalActive,
    },
    catalog,
    discovered_slugs: discovered,
    merged_for_audience_picker: merged,
    active_recipient_counts_by_channel: perChannelCounts,
  };

  const json = JSON.stringify(payload, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      toast.success('Copied JSON to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Channels (debug)</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Read-only JSON view of the channel catalog and live subscriber data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => void copy()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy JSON
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200 mb-4">
          {error}
        </div>
      ) : null}

      <pre className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 text-xs font-mono text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words">
        {loading && discovered.length === 0 ? 'Loading…' : json}
      </pre>
    </div>
  );
}
